import axios from 'axios';
import { ipLiteralVersion } from '../util/ip-literal.js';
import { DocumentLoadError, assertJsonObject } from './document-loader';
import { assertResponseOrigin } from './response-origin.js';
import { initLogger } from '../logger';
const DEFAULT_ALLOWED_REMOTE_HOSTS = ['calm.finos.org'];
const PRIVATE_IPV4_PATTERNS = [
    /^127\./,
    /^10\./,
    /^172\.(1[6-9]|2\d|3[01])\./,
    /^192\.168\./,
    /^0\./,
    /^169\.254\./,
];
const PRIVATE_IPV6_PATTERNS = [
    /^::1$/,
    /^fc/i,
    /^fd/i,
    /^fe80:/i,
];
// Mirrors CalmHubDocumentLoader.SAFE_PATH_PATTERN: a strict character allowlist for the request
// path, checked in addition to (not instead of) the host allowlist below.
const SAFE_PATH_PATTERN = /^[a-zA-Z0-9/_.-]+$/;
const TLS_CERTIFICATE_ERROR_CODES = new Set([
    'ERR_TLS_CERT_ALTNAME_INVALID',
    'CERT_HAS_EXPIRED',
    'CERT_NOT_YET_VALID',
    'DEPTH_ZERO_SELF_SIGNED_CERT',
    'SELF_SIGNED_CERT_IN_CHAIN',
    'UNABLE_TO_GET_ISSUER_CERT',
    'UNABLE_TO_GET_ISSUER_CERT_LOCALLY',
    'UNABLE_TO_VERIFY_LEAF_SIGNATURE',
]);
const TLS_CERTIFICATE_ERROR_MESSAGES = [
    /Hostname\/IP does not match certificate's altnames/,
    /self-signed certificate/i,
    /certificate has expired/i,
    /certificate is not yet valid/i,
    /unable to verify the first certificate/i,
    /unable to get local issuer certificate/i,
    /unable to verify leaf signature/i,
];
function isPrivateHost(hostname) {
    if (/^localhost$/i.test(hostname))
        return true;
    // URL.hostname wraps IPv6 in brackets; strip them for ipLiteralVersion/pattern checks
    const bare = hostname.startsWith('[') && hostname.endsWith(']')
        ? hostname.slice(1, -1)
        : hostname;
    const ipVersion = ipLiteralVersion(bare);
    if (ipVersion === 4)
        return PRIVATE_IPV4_PATTERNS.some(p => p.test(bare));
    if (ipVersion === 6)
        return PRIVATE_IPV6_PATTERNS.some(p => p.test(bare));
    return false;
}
function normalizeHost(hostname) {
    const bare = hostname.startsWith('[') && hostname.endsWith(']')
        ? hostname.slice(1, -1)
        : hostname;
    return bare.toLowerCase();
}
function toRequestPath(parsedUrl) {
    const normalizedPath = parsedUrl.pathname.replace(/^\/+/, '');
    return `/${normalizedPath}`;
}
function normalizeHeaderValue(value) {
    if (Array.isArray(value)) {
        return value.map(item => normalizeHeaderValue(item));
    }
    if (value === null
        || value === undefined
        || typeof value === 'string'
        || typeof value === 'number'
        || typeof value === 'boolean') {
        return value;
    }
    return JSON.stringify(value);
}
function collectSafeHeaders(headers, authHeaderNames) {
    const candidate = typeof headers === 'object' && headers !== null && 'toJSON' in headers && typeof headers.toJSON === 'function'
        ? headers.toJSON()
        : headers;
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
        return {};
    }
    const authHeaderNameSet = new Set(authHeaderNames.map(name => name.toLowerCase()));
    return Object.fromEntries(Object.entries(candidate)
        .filter(([key]) => !authHeaderNameSet.has(key.toLowerCase()))
        .map(([key, value]) => [
        key,
        normalizeHeaderValue(value),
    ]));
}
function resolveLoggedUrl(baseURL, url) {
    const base = typeof baseURL === 'string' ? baseURL : undefined;
    const path = typeof url === 'string' ? url : undefined;
    if (base && path) {
        try {
            return new URL(path, base).toString();
        }
        catch {
            return `${base}${path}`;
        }
    }
    return base ?? path;
}
function sanitizeTlsCertificateError(error, hostname) {
    const visited = new Set();
    let current = error;
    while (current instanceof Error && !visited.has(current)) {
        const currentError = current;
        visited.add(currentError);
        const code = 'code' in currentError ? currentError.code : undefined;
        if ((typeof code === 'string' && TLS_CERTIFICATE_ERROR_CODES.has(code))
            || TLS_CERTIFICATE_ERROR_MESSAGES.some(pattern => pattern.test(currentError.message))) {
            return new Error(`TLS certificate verification failed for ${hostname}.`);
        }
        current = 'cause' in currentError ? currentError.cause : undefined;
    }
    return undefined;
}
function documentLoadCause(error, hostname) {
    return sanitizeTlsCertificateError(error, hostname) ?? (error instanceof Error ? error : undefined);
}
export class DirectUrlDocumentLoader {
    constructor(debug, axiosInstance, allowedRemoteHosts = DEFAULT_ALLOWED_REMOTE_HOSTS, directUrlAuthPlugin, directUrlAuthAuthenticatedHosts) {
        if (axiosInstance) {
            this.ax = axiosInstance;
        }
        else {
            this.ax = axios.create({
                timeout: 10000,
                headers: { 'Content-Type': 'application/json' }
            });
        }
        this.logger = initLogger(debug, 'direct-url-document-loader');
        this.directUrlAuthAuthenticatedHosts = directUrlAuthAuthenticatedHosts
            ? new Set(directUrlAuthAuthenticatedHosts.map(host => normalizeHost(host)))
            : undefined;
        this.allowedRemoteHosts = new Set([
            ...allowedRemoteHosts.map(host => normalizeHost(host)),
            ...(this.directUrlAuthAuthenticatedHosts ?? []),
        ]);
        this.directUrlAuthPlugin = directUrlAuthPlugin;
        if (debug) {
            this.addAxiosDebug();
        }
    }
    addAxiosDebug() {
        this.ax.interceptors.request.use(request => {
            const authHeaderNames = [...(request.__directUrlAuthHeaderNames ?? [])].sort();
            this.logger.debug(`Starting Request: ${JSON.stringify({
                method: request.method,
                url: resolveLoggedUrl(request.baseURL, request.url),
                baseURL: request.baseURL,
                path: request.url,
                timeout: request.timeout,
                maxRedirects: request.maxRedirects,
                allowAbsoluteUrls: request.allowAbsoluteUrls,
                headers: collectSafeHeaders(request.headers, authHeaderNames),
                authHeadersPresent: authHeaderNames.length > 0,
                authHeaderNames,
            }, null, 2)}`);
            return request;
        });
        this.ax.interceptors.response.use(response => {
            this.logger.debug(`Response: ${JSON.stringify({
                status: response.status,
                statusText: response.statusText,
                url: resolveLoggedUrl(response.config?.baseURL, response.config?.url),
            }, null, 2)}`);
            return response;
        });
    }
    async initialise(_) {
        // No-op, similar to CalmHubDocumentLoader
        return;
    }
    async loadMissingDocument(documentId, _type) {
        let parsedUrl;
        try {
            parsedUrl = new URL(documentId);
        }
        catch {
            // Not a parseable absolute URL — recoverable, let other loaders try.
            throw new DocumentLoadError({
                name: 'UNKNOWN',
                message: `Not a valid absolute URL: ${documentId}`,
            });
        }
        const allowedProtocols = ['http:', 'https:'];
        if (!allowedProtocols.includes(parsedUrl.protocol)) {
            // Not an HTTP(S) reference — recoverable, let other loaders try.
            throw new DocumentLoadError({
                name: 'UNKNOWN',
                message: `Unsupported URL protocol '${parsedUrl.protocol}' in document URL. Only HTTP and HTTPS are allowed.`,
            });
        }
        // From here the reference is ours (an HTTP(S) URL): any failure is fatal and must not fall
        // through to another loader (which would mask the real reason with an unrelated error).
        try {
            if (isPrivateHost(parsedUrl.hostname)) {
                throw new DocumentLoadError({
                    name: 'UNKNOWN',
                    message: `Requests to private or internal network addresses are not allowed: ${parsedUrl.hostname}`,
                    recoverable: false
                });
            }
            const normalizedHost = normalizeHost(parsedUrl.hostname);
            if (!this.allowedRemoteHosts.has(normalizedHost)) {
                throw new DocumentLoadError({
                    name: 'UNKNOWN',
                    message: `Direct URL loading is restricted to approved hosts. Host '${parsedUrl.hostname}' is not allowlisted.\n\n`
                        + 'To allow this host, run:\n\n'
                        + `  calm init-config --allowed-remote-hosts ${parsedUrl.hostname}\n\n`
                        + 'Only add hosts you trust.',
                    recoverable: false
                });
            }
            if (parsedUrl.username || parsedUrl.password) {
                throw new DocumentLoadError({
                    name: 'UNKNOWN',
                    message: 'Credentials in URL are not allowed.',
                    recoverable: false
                });
            }
            // The URL constructor normalizes '..' segments, so parsedUrl.pathname is already
            // resolved. Reject if the original input contained traversal sequences before
            // normalization, rather than silently trusting the normalized result.
            if (documentId.includes('/..')) {
                throw new DocumentLoadError({
                    name: 'UNKNOWN',
                    message: `Direct URL loading rejected a path containing directory traversal: ${documentId}`,
                    recoverable: false
                });
            }
            const requestPath = toRequestPath(parsedUrl);
            if (!SAFE_PATH_PATTERN.test(requestPath)) {
                throw new DocumentLoadError({
                    name: 'UNKNOWN',
                    message: `Direct URL loading rejected a path with disallowed characters: ${requestPath}`,
                    recoverable: false
                });
            }
            if (parsedUrl.search) {
                throw new DocumentLoadError({
                    name: 'UNKNOWN',
                    message: `Direct URL loading does not support a query string: ${documentId}`,
                    recoverable: false
                });
            }
            const baseURL = `${parsedUrl.protocol}//${normalizedHost}${parsedUrl.port ? `:${parsedUrl.port}` : ''}`;
            let authHeaders;
            const authHeaderNames = [];
            if (this.directUrlAuthPlugin && (!this.directUrlAuthAuthenticatedHosts || this.directUrlAuthAuthenticatedHosts.has(normalizedHost))) {
                try {
                    authHeaders = await this.directUrlAuthPlugin.getAuthHeaders(`${baseURL}${requestPath}`, undefined);
                    authHeaderNames.push(...Object.keys(authHeaders));
                }
                catch (error) {
                    throw new DocumentLoadError({
                        name: 'AUTHENTICATION_FAILED',
                        message: `Direct URL authentication failed for ${documentId}. Check direct URL auth configuration and remote credentials.`,
                        cause: documentLoadCause(error, parsedUrl.hostname),
                        recoverable: false
                    });
                }
            }
            const requestConfig = {
                baseURL,
                headers: authHeaders,
                maxRedirects: 0,
                allowAbsoluteUrls: false,
                __directUrlAuthHeaderNames: authHeaderNames,
            };
            const response = await this.ax.get(requestPath, requestConfig);
            assertResponseOrigin(response, new URL(baseURL).origin, documentId);
            assertJsonObject(response.data, documentId);
            return response.data;
        }
        catch (error) {
            if (error instanceof DocumentLoadError) {
                throw error;
            }
            if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
                throw new DocumentLoadError({
                    name: 'AUTHENTICATION_FAILED',
                    message: `Direct URL request was not authorized for ${documentId} (HTTP ${error.response.status}). Check direct URL auth configuration and remote credentials.`,
                    cause: error,
                    recoverable: false
                });
            }
            throw new DocumentLoadError({
                name: 'UNKNOWN',
                message: `Failed to load document from URL: ${documentId}`,
                cause: documentLoadCause(error, parsedUrl.hostname),
                recoverable: false
            });
        }
    }
    /**
     * Only local files via a mapping file are currently supported.
     */
    resolvePath(_reference) {
        return undefined;
    }
}
//# sourceMappingURL=direct-url-document-loader.js.map